import { isRelation } from '@contember/schema-utils';
import { noCase, pascalCase, sentenceCase } from 'change-case';
import { emptySchemaExtensions, printStmtArguments, } from '../common';
import { getAllHumanFriendlyFields, getHumanFriendlyRelationFields, StructureBuilder } from './builder/StructureBuilder';
import { emptyStructure } from './emptyStructure';
import { getDefaultColumnType, getDefaultFieldType, isCreateScope, isDataGridScope, isDeleteEvent, isDetailScope, isEditScope, isNavigateEvent, isPersistEvent, isPluginScope, isRedirectOnPersistEvent, } from './helpers';
import { stmtAclAllowScope, stmtAddFilterScope, stmtAddPreview, stmtAddPreviewField, stmtAddReadoutField, stmtAddStructureColumn, stmtAddStructureComment, stmtAddStructureField, stmtAddStructurePlugin, stmtCreateForm, stmtCreateInsideRepeaterForm, stmtCreateInsideSelectForm, stmtCreateNavigation, stmtCreatePreview, stmtCreateReadout, stmtCreateScope, stmtCreateView, stmtDefineClickEvent, stmtDefineRedirectOnPersistEvent, } from './stmts';
import { isStructureColumnType } from './types/helpers';
import { CrudAnalyzer } from './utils/determine-crud-requirements';
import { getFormName, getPreviewName, getReadoutName } from './utils/nameFormat';
export class AppStructureStmtPrinterError extends Error {
}
export class AppStructureStmtPrinter {
    constructor() {
        Object.defineProperty(this, "schema", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "schemaExtensions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: emptySchemaExtensions
        });
        Object.defineProperty(this, "application", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: emptyStructure
        });
        Object.defineProperty(this, "structureBuilder", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "getModelEntities", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (plugin) => Object.keys(plugin.schema?.model.entities ?? {})
        });
    }
    setSchema(newSchema) {
        this.schema = newSchema;
    }
    getSchema() {
        return this.schema;
    }
    setSchemaExtensions(newSchemaExtensions) {
        this.schemaExtensions = newSchemaExtensions;
    }
    getSchemaExtensions() {
        return this.schemaExtensions;
    }
    setApplication(newApplication) {
        this.application = newApplication;
    }
    getApplication() {
        return this.application;
    }
    setStructureBuilder(newStructureBuilder) {
        this.structureBuilder = newStructureBuilder;
    }
    print(application) {
        this.setApplication(application);
        const stmts = [
            ...this.printCustomViewsCreate(),
            ...this.printCustomFormsCreate(),
            ...this.printCustomPreviewsCreate(),
            ...this.printCustomReadoutsCreate(),
            ...this.printCustomScopesCreate(),
            ...this.printCustomScopeElements(),
            ...this.printCustomFormFieldsCreate(),
            ...this.printCustomPreviewFieldsCreate(),
            ...this.printCustomReadoutFieldsCreate(),
            ...this.printCustomEventDefine(),
            ...this.printCustomNavigationCreate(),
        ];
        return stmts.map(it => `${it}\n`).join('');
    }
    printCRUD(schema, schemaExtensions, applicationType) {
        if (!schema)
            return '';
        this.setSchema(schema);
        this.setSchemaExtensions(schemaExtensions);
        return this.print(this.getCrudAppStructure(applicationType));
    }
    printCustomViewsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const view of Object.values(structure.views)) {
            stmts.push(...this.printCreateView(view));
            if (view.comments) {
                for (const comment of view.comments) {
                    stmts.push(stmtAddStructureComment({ comment, viewName: view.name }));
                }
            }
        }
        return stmts;
    }
    printCustomFormsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const form of Object.values(structure.forms)) {
            stmts.push(stmtCreateForm({ formName: form.name, entityName: form.entityName }));
        }
        return stmts;
    }
    printCustomPreviewsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const preview of Object.values(structure.previews)) {
            stmts.push(stmtCreatePreview({ previewName: preview.name, entityName: preview.entityName }));
        }
        return stmts;
    }
    printCustomReadoutsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const detail of Object.values(structure.readouts)) {
            stmts.push(stmtCreateReadout({ readoutName: detail.name, entityName: detail.entityName }));
        }
        return stmts;
    }
    printCustomScopesCreate() {
        const structure = this.application;
        const stmts = [];
        for (const view of Object.values(structure.views)) {
            const scopes = Object.values(view.scopes);
            if (scopes.length === 0) {
                continue;
            }
            for (const scope of scopes) {
                stmts.push(...this.printCreateScope(scope, view));
            }
        }
        return stmts;
    }
    printCustomScopeElements() {
        const structure = this.application;
        const stmts = [];
        for (const view of Object.values(structure.views)) {
            const scopes = Object.values(view.scopes);
            if (scopes.length === 0) {
                continue;
            }
            for (const scope of scopes) {
                if (isPluginScope(scope)) {
                    continue;
                }
                if (isDataGridScope(scope)) {
                    for (const column of Object.values(scope.columns ?? {})) {
                        stmts.push(stmtAddStructureColumn({
                            columnName: column.field,
                            columnType: isStructureColumnType(column.type) ? column.type : getDefaultColumnType(column.modelField),
                            viewName: view.name,
                            scopeName: scope.name,
                            pluginParams: column.type === 'plugin' && 'pluginArguments' in column ? printStmtArguments(column.pluginArguments) : undefined,
                        }));
                    }
                }
                else if (isDetailScope(scope)) {
                    for (const readoutName of scope.readouts ?? []) {
                        stmts.push(stmtCreateReadout({
                            readoutName,
                            viewName: view.name,
                            scopeName: scope.name,
                            entityName: scope.entityName,
                        }));
                    }
                }
                else {
                    for (const formName of scope.forms ?? []) {
                        stmts.push(stmtCreateForm({
                            formName,
                            viewName: view.name,
                            scopeName: scope.name,
                            entityName: scope.entityName,
                        }));
                    }
                }
            }
        }
        return stmts;
    }
    printCustomFormFieldsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const form of Object.values(structure.forms)) {
            for (const field of Object.values(form.fields)) {
                stmts.push(stmtAddStructureField({
                    fieldName: field.name,
                    fieldType: field.type,
                    formName: form.name,
                    pluginParams: field.type === 'plugin' && field.pluginArguments ? printStmtArguments(field.pluginArguments) : undefined,
                }));
                if (field.type === 'select' || field.type === 'multiselect' || field.type === 'repeater') {
                    if (isRelation(field.modelField)) {
                        const targetEntity = field.modelField.target;
                        if (field.type !== 'repeater') {
                            stmts.push(stmtAddPreview({
                                previewName: getPreviewName(field.name),
                                formName: form.name,
                                fieldName: field.name,
                                entityName: targetEntity,
                            }));
                            stmts.push(stmtCreateInsideSelectForm({
                                fieldName: field.name,
                                insideFormName: getFormName(targetEntity),
                                formName: form.name,
                            }));
                        }
                        else {
                            stmts.push(stmtCreateInsideRepeaterForm({
                                fieldName: field.name,
                                insideFormName: getFormName(targetEntity),
                                formName: form.name,
                            }));
                        }
                    }
                }
            }
        }
        return stmts;
    }
    printCustomReadoutFieldsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const readout of Object.values(structure.readouts)) {
            for (const field of Object.values(readout.fields)) {
                stmts.push(stmtAddReadoutField({ readoutFieldName: field.name, readoutName: readout.name }));
            }
        }
        return stmts;
    }
    printCustomPreviewFieldsCreate() {
        const structure = this.application;
        const stmts = [];
        for (const preview of Object.values(structure.previews)) {
            for (const field of Object.values(preview.fields)) {
                stmts.push(stmtAddPreviewField({ previewFieldName: field.name, previewName: preview.name }));
            }
        }
        return stmts;
    }
    printCustomEventDefine() {
        const structure = this.application;
        const generateStatementsForEvents = (events, viewName, scopeName) => {
            return {
                navigation: events.filter(isNavigateEvent).map(event => stmtDefineClickEvent({
                    eventName: event.name,
                    eventLabel: event.label,
                    sourceViewName: viewName,
                    sourceScopeName: scopeName,
                    eventType: 'navigate',
                    eventPosition: event.position,
                    targetViewName: event.targetViewName,
                })),
                persist: events.filter(isPersistEvent).map(event => stmtDefineClickEvent({
                    eventName: event.name,
                    eventLabel: event.label,
                    sourceViewName: viewName,
                    sourceScopeName: scopeName,
                    eventType: 'persist',
                    eventPosition: event.position,
                })),
                delete: events.filter(isDeleteEvent).map(event => stmtDefineClickEvent({
                    eventName: event.name,
                    eventLabel: event.label,
                    sourceViewName: viewName,
                    sourceScopeName: scopeName,
                    eventType: 'delete',
                    eventPosition: event.position,
                })),
                redirectOnPersist: events.filter(isRedirectOnPersistEvent).map(event => stmtDefineRedirectOnPersistEvent({
                    eventName: event.name,
                    sourceViewName: viewName,
                    sourceScopeName: scopeName,
                    targetViewName: event.targetViewName,
                })),
            };
        };
        let stmts = [];
        for (const view of Object.values(structure.views)) {
            const viewEvents = Object.values(view.events || {});
            const viewEventsStmts = generateStatementsForEvents(viewEvents, view.name);
            stmts = stmts.concat(Object.values(viewEventsStmts).flat());
            for (const scope of Object.values(view.scopes)) {
                const scopeEvents = Object.values(scope.events || {});
                const scopeEventsStmts = generateStatementsForEvents(scopeEvents, view.name, scope.name);
                stmts = stmts.concat(Object.values(scopeEventsStmts).flat());
            }
        }
        return stmts;
    }
    printCustomNavigationCreate() {
        const navigation = this.application.navigation;
        const stmts = [];
        const processItem = (item, prefix = '') => {
            const fullLabel = prefix ? `"${prefix}"->"${item.label}"` : `"${item.label}"`;
            stmts.push(stmtCreateNavigation({ label: fullLabel, viewName: item.viewName, icon: item.icon }));
            if (item.children && item.children.length > 0) {
                for (const child of item.children) {
                    processItem(child, fullLabel);
                }
            }
        };
        for (const item of navigation) {
            processItem(item);
        }
        return stmts;
    }
    printCreateView(view) {
        const stmts = [stmtCreateView({ viewName: view.name })];
        const plugins = view.plugins;
        if (plugins) {
            for (const plugin of Object.values(plugins)) {
                if (plugin.arguments) {
                    stmts.push(stmtAddStructurePlugin({
                        pluginName: plugin.name,
                        viewName: view.name,
                        params: plugin.arguments,
                    }));
                }
            }
        }
        return stmts;
    }
    printCreateScope(scope, view) {
        const stmts = [stmtCreateScope({
                scopeName: scope.name,
                scopeType: scope.type,
                viewName: view.name,
                entityName: scope.entityName,
            })];
        if (isDataGridScope(scope) && scope.filter) {
            stmts.push(stmtAddFilterScope({ scopeName: scope.name, viewName: view.name, filterQuery: scope.filter }));
            for (const column of Object.values(scope.columns ?? {})) {
                stmts.push(stmtAddStructureColumn({
                    columnName: column.field,
                    columnType: isStructureColumnType(column.type) ? column.type : getDefaultColumnType(column.modelField),
                    viewName: view.name,
                    scopeName: scope.name,
                }));
            }
        }
        if (scope.plugins) {
            for (const plugin of Object.values(scope.plugins)) {
                if (plugin.arguments) {
                    stmts.push(stmtAddStructurePlugin({
                        pluginName: plugin.name,
                        viewName: view.name,
                        scopeName: scope.name,
                        params: plugin.arguments,
                    }));
                }
            }
        }
        if (scope.comments) {
            for (const comment of scope.comments) {
                stmts.push(stmtAddStructureComment({ comment, viewName: view.name, scopeName: scope.name }));
            }
        }
        if ('forms' in scope && scope.forms) {
            for (const formName of scope.forms) {
                stmts.push(stmtCreateForm({
                    formName,
                    viewName: view.name,
                    scopeName: scope.name,
                    entityName: scope.entityName,
                }));
            }
        }
        if (scope.roles?.length) {
            stmts.push(stmtAclAllowScope({ roles: scope.roles, viewName: view.name, scopeName: scope.name }));
        }
        return stmts;
    }
    getPluginEntityNames() {
        const schemaExtensions = this.schemaExtensions;
        const plugins = schemaExtensions.plugins ?? {};
        return [...new Set(Object.values(plugins).flatMap(this.getModelEntities))];
    }
    getPluginsByEntity() {
        const schemaExtensions = this.schemaExtensions;
        return Object.values(schemaExtensions.plugins ?? {}).reduce((acc, plugin) => {
            for (const [entityName, { fields, params }] of Object.entries(plugin.connectedTo)) {
                acc[entityName] = acc[entityName] ?? { [plugin.name]: { name: plugin.name, type: plugin.type } };
                if (!acc[entityName][plugin.name]) {
                    acc[entityName][plugin.name] = {
                        name: plugin.name,
                        type: plugin.type,
                    };
                }
                if (fields) {
                    if (!acc[entityName][plugin.name].fields) {
                        acc[entityName][plugin.name].fields = [];
                    }
                    acc[entityName][plugin.name].fields = fields;
                }
                if (params) {
                    if (!acc[entityName][plugin.name].arguments) {
                        acc[entityName][plugin.name].arguments = {};
                    }
                    acc[entityName][plugin.name].arguments = params;
                }
            }
            return acc;
        }, {});
    }
    getEntityPlugins(entityName, fieldName) {
        const pluginsByEntity = this.getPluginsByEntity();
        const entityPlugins = pluginsByEntity[entityName] || {};
        return entityPlugins[fieldName];
    }
    getCrudAppStructure(applicationType) {
        const schema = this.schema;
        const schemaExtensions = this.schemaExtensions ?? emptySchemaExtensions;
        const analyzer = new CrudAnalyzer(schema, schemaExtensions, applicationType);
        const crudDecisions = analyzer.determineCrudRequirements();
        if (!schema) {
            throw new AppStructureStmtPrinterError('Schema is not set');
        }
        const structureBuilder = new StructureBuilder(emptyStructure, schema, schemaExtensions);
        this.setStructureBuilder(structureBuilder);
        const entities = Object.values(schema.model.entities);
        const scopeTypes = crudDecisions.scopeTypes;
        const pluginsByEntity = this.getPluginsByEntity();
        for (const entity of entities) {
            structureBuilder.addForm({ formName: getFormName(entity.name), entityName: entity.name });
            structureBuilder.addReadout({ readoutName: getReadoutName(entity.name), entityName: entity.name });
            structureBuilder.addPreview({ previewName: getPreviewName(entity.name), entityName: entity.name });
            const allFields = Object.values(entity.fields);
            const allHumanFriendlyFields = getAllHumanFriendlyFields(entity);
            for (const field of allFields) {
                if (this.isFieldSkippable(field)) {
                    continue;
                }
                structureBuilder.addFormField({
                    formName: getFormName(entity.name),
                    fieldName: field.name,
                    fieldType: getDefaultFieldType(field),
                });
                structureBuilder.addReadoutField({
                    readoutName: getReadoutName(entity.name),
                    readoutFieldName: field.name,
                });
            }
            if (allHumanFriendlyFields.length > 0) {
                for (const fieldName of allHumanFriendlyFields) {
                    structureBuilder.addPreviewField({
                        previewName: getPreviewName(entity.name),
                        previewFieldName: fieldName,
                    });
                }
                continue;
            }
            const humanFriendlyRelationFields = getHumanFriendlyRelationFields(entity, schema.model.entities);
            const relationFieldNames = Object.entries(humanFriendlyRelationFields);
            if (relationFieldNames.length > 0) {
                for (const [relationField, fieldNames] of relationFieldNames) {
                    if (Array.isArray(fieldNames) && fieldNames.length === 0) {
                        for (const fieldName of fieldNames) {
                            structureBuilder.addPreviewField({
                                previewName: getPreviewName(entity.name),
                                previewFieldName: `${relationField}.${fieldName}`,
                            });
                        }
                    }
                }
                continue;
            }
            structureBuilder.addPreviewField({ previewName: getPreviewName(entity.name), previewFieldName: 'id' });
        }
        for (const entity of entities) {
            if (!crudDecisions.entities[entity.name].needsCrud) {
                continue;
            }
            const { name, fields } = entity;
            const elements = Object.values(fields);
            const formName = getFormName(name);
            const readoutName = getReadoutName(name);
            const plugins = pluginsByEntity[name] ?? {};
            for (const scopeType of scopeTypes) {
                const viewName = name + pascalCase(scopeType === 'dataGrid' ? 'list' : scopeType);
                const scopeName = pascalCase(name);
                const roleNames = Object.keys(schema.acl.roles);
                const roleNamesWithReadAccess = this.getRoleNamesWithAccess(schema, entity, 'read', elements);
                const roleNamesWithCreateAccess = this.getRoleNamesWithAccess(schema, entity, 'create', elements);
                const roleNamesWithUpdateAccess = this.getRoleNamesWithAccess(schema, entity, 'update', elements);
                this.handleScopeType(scopeType, {
                    viewName,
                    scopeName,
                    entityName: name,
                    formName,
                    readoutName,
                    roleNames,
                    roleNamesWithReadAccess,
                    roleNamesWithCreateAccess,
                    roleNamesWithUpdateAccess,
                });
                this.handleElements(scopeType, elements, {
                    viewName,
                    scopeName,
                    formName,
                    readoutName,
                    plugins,
                });
            }
        }
        this.handleEvents(structureBuilder);
        return structureBuilder.application;
    }
    getRoleNamesWithAccess(schema, entity, accessType, elements) {
        return Object.entries(schema.acl.roles)
            .filter(([role, permissions]) => {
            const operations = getMatchingOperations({
                operations: permissions.entities[entity.name]?.operations,
                predicate: true,
                numberOfEntityFieldsWithoutId: elements.length - 1,
            });
            return !!operations[accessType];
        })
            .map(([role]) => role);
    }
    handleScopeType(scopeType, { viewName, scopeName, entityName, formName, readoutName, roleNames, roleNamesWithReadAccess, roleNamesWithCreateAccess, roleNamesWithUpdateAccess, }) {
        switch (scopeType) {
            case 'dataGrid':
                this.handleDataGridScope({ viewName, scopeName, entityName, roleNames, roleNamesWithReadAccess });
                break;
            case 'detail':
                this.handleDetailScope({
                    viewName,
                    scopeName,
                    entityName,
                    readoutName,
                    roleNames,
                    roleNamesWithReadAccess,
                });
                break;
            case 'create':
                this.handleCreateScope({
                    viewName,
                    scopeName,
                    entityName,
                    formName,
                    roleNames,
                    roleNamesWithCreateAccess,
                });
                break;
            case 'edit':
                this.handleEditScope({
                    viewName,
                    scopeName,
                    entityName,
                    formName,
                    roleNames,
                    roleNamesWithUpdateAccess,
                });
                break;
            default:
                throw new AppStructureStmtPrinterError(`Unknown scope type: ${scopeType}`);
        }
    }
    handleDataGridScope({ viewName, scopeName, entityName, roleNames, roleNamesWithReadAccess, }) {
        const structureBuilder = this.structureBuilder;
        if (!structureBuilder) {
            throw new AppStructureStmtPrinterError('Structure builder is not set');
        }
        structureBuilder
            .addView(viewName)
            .addScope({ scopeName, viewName, scopeType: 'dataGrid', entityName })
            .addNavigationItem({ label: sentenceCase(viewName), viewName });
        if (roleNamesWithReadAccess.length > 0) {
            structureBuilder.allowScope({ roles: roleNamesWithReadAccess, viewName, scopeName });
        }
        else if (roleNames.length > 0) {
            structureBuilder.denyScope({ roles: roleNames, viewName, scopeName });
        }
    }
    handleDetailScope({ viewName, scopeName, entityName, readoutName, roleNames, roleNamesWithReadAccess, }) {
        const structureBuilder = this.structureBuilder;
        if (!structureBuilder) {
            throw new AppStructureStmtPrinterError('Structure builder is not set');
        }
        structureBuilder
            .addView(viewName)
            .addScope({ scopeName, viewName, scopeType: 'detail', entityName })
            .addReadout({ readoutName, viewName, scopeName, entityName });
        if (roleNamesWithReadAccess.length > 0) {
            structureBuilder.allowScope({ roles: roleNamesWithReadAccess, viewName, scopeName });
        }
        else if (roleNames.length > 0) {
            structureBuilder.denyScope({ roles: roleNames, viewName, scopeName });
        }
    }
    handleCreateScope({ viewName, scopeName, entityName, formName, roleNames, roleNamesWithCreateAccess, }) {
        const structureBuilder = this.structureBuilder;
        if (!structureBuilder) {
            throw new AppStructureStmtPrinterError('Structure builder is not set');
        }
        structureBuilder
            .addView(viewName)
            .addScope({ scopeName, viewName, scopeType: 'create', entityName })
            .addForm({ formName, viewName, scopeName, entityName });
        if (roleNamesWithCreateAccess.length > 0) {
            structureBuilder.allowScope({ roles: roleNamesWithCreateAccess, viewName, scopeName });
        }
        else if (roleNames.length > 0) {
            structureBuilder.denyScope({ roles: roleNames, viewName, scopeName });
        }
    }
    handleEditScope({ viewName, scopeName, entityName, formName, roleNames, roleNamesWithUpdateAccess, }) {
        const structureBuilder = this.structureBuilder;
        if (!structureBuilder) {
            throw new AppStructureStmtPrinterError('Structure builder is not set');
        }
        structureBuilder
            .addView(viewName)
            .addScope({ scopeName, viewName, scopeType: 'edit', entityName })
            .addForm({ formName, viewName, scopeName, entityName });
        if (roleNamesWithUpdateAccess.length > 0) {
            structureBuilder.allowScope({ roles: roleNamesWithUpdateAccess, viewName, scopeName });
        }
        else if (roleNames.length > 0) {
            structureBuilder.denyScope({ roles: roleNames, viewName, scopeName });
        }
    }
    handleElements(scopeType, elements, { viewName, scopeName, formName, readoutName, plugins, }) {
        for (const element of elements) {
            if (this.isFieldSkippable(element)) {
                continue;
            }
            const structureBuilder = this.structureBuilder;
            if (!structureBuilder) {
                throw new AppStructureStmtPrinterError('Structure builder is not set');
            }
            if (scopeType === 'dataGrid') {
                const isPluginField = Object.values(plugins).find(plugin => plugin.fields?.includes(element.name));
                if (isPluginField) {
                    // TODO: add plugin column
                    // structureBuilder.addColumn({ columnName: element.name, scopeName, viewName, columnType: 'plugin', pluginParams: isPluginField?.arguments })
                }
                else {
                    structureBuilder.addColumn({
                        columnName: element.name,
                        scopeName,
                        viewName,
                        columnType: getDefaultColumnType(element),
                    });
                }
                continue;
            }
            const plugin = Object.values(plugins).find(plugin => plugin.fields?.includes(element.name));
            const fieldType = plugin ? 'plugin' : getDefaultFieldType(element);
            if (scopeType === 'detail') {
                // Skip relations to plugin entities for details
                if (isRelation(element) && this.getPluginEntityNames().includes(element.target)) {
                    continue;
                }
                structureBuilder.addReadoutField({
                    readoutFieldName: element.name,
                    plugin,
                    readoutName: readoutName,
                    readoutType: fieldType,
                });
                continue;
            }
            structureBuilder.addFormField({
                fieldName: element.name,
                plugin,
                formName,
                fieldType,
                pluginParams: plugin?.arguments,
            });
            this.handleSelectField(structureBuilder, element, formName, plugins);
        }
    }
    handleSelectField(structureBuilder, element, formName, plugins) {
        const plugin = Object.values(plugins).find(plugin => plugin.fields?.includes(element.name));
        const fieldType = plugin ? 'plugin' : getDefaultFieldType(element);
        structureBuilder.addFormField({
            fieldName: element.name,
            plugin,
            formName,
            fieldType,
            pluginParams: plugin?.arguments,
        });
        if (isRelation(element) && (fieldType === 'select' || fieldType === 'multiselect')) {
            const previewName = getPreviewName(element.name);
            const target = element.target;
            const entity = this.schema?.model.entities[target];
            if (!entity) {
                throw new AppStructureStmtPrinterError(`Entity ${target} not found.`);
            }
        }
    }
    handleEvents(structureBuilder) {
        const views = structureBuilder.application.views;
        const viewKeys = Object.keys(views);
        const viewsArray = Object.values(views);
        const hasScope = (view, type) => Object.values(view.scopes).some(scope => scope.type === type);
        const getEntityName = (viewName) => viewName.replace(/(Create|Edit|Detail|List)$/, '');
        const viewExists = (viewName, suffix) => viewKeys.includes(`${viewName}${suffix}`);
        const getTargetViewName = (entityName) => {
            const suffixes = ['Detail', 'Edit', 'List'];
            for (const suffix of suffixes) {
                if (viewExists(entityName, suffix)) {
                    return `${entityName}${suffix}`;
                }
            }
            return entityName;
        };
        for (const view of viewsArray) {
            const entityName = getEntityName(view.name);
            if (hasScope(view, 'edit') || hasScope(view, 'create')) {
                this.addPersistEvent(structureBuilder, view.name);
            }
            if ((hasScope(view, 'create') && viewExists(entityName, 'Detail')) || viewExists(entityName, 'Edit') || viewExists(entityName, 'List')) {
                const createScopes = Object.values(view.scopes).filter(scope => scope.type === 'create');
                for (const createScope of createScopes) {
                    structureBuilder.addEvent({
                        eventName: `${view.name}RedirectOnSuccess`,
                        eventType: 'redirectOnPersist',
                        sourceViewName: view.name,
                        sourceScopeName: createScope.name,
                        eventPosition: 'entity',
                        targetViewName: getTargetViewName(entityName),
                    });
                }
            }
            if (hasScope(view, 'dataGrid')) {
                const dataGridScopes = Object.values(view.scopes).filter(scope => scope.type === 'dataGrid');
                for (const dataGridScope of dataGridScopes) {
                    if (viewExists(entityName, 'Detail')) {
                        this.addNavigateEvent(structureBuilder, view.name, dataGridScope.name, `${entityName}Detail`, 'Detail', 'entity');
                    }
                    if (viewExists(entityName, 'Edit')) {
                        this.addNavigateEvent(structureBuilder, view.name, dataGridScope.name, `${entityName}Edit`, 'Edit', 'entity');
                    }
                    if (viewExists(entityName, 'Create')) {
                        this.addNavigateEvent(structureBuilder, view.name, dataGridScope.name, `${entityName}Create`, `Create ${entityName}`);
                    }
                }
            }
            if (hasScope(view, 'detail')) {
                const detailScopes = Object.values(view.scopes).filter(scope => scope.type === 'detail');
                const label = `Edit ${noCase(entityName)}`;
                for (const detailScope of detailScopes) {
                    this.addNavigateEvent(structureBuilder, view.name, detailScope.name, `${entityName}Edit`, label);
                }
            }
            if (hasScope(view, 'edit')) {
                const editScopes = Object.values(view.scopes).filter(scope => scope.type === 'edit');
                for (const editScope of editScopes) {
                    this.addDeleteEvent(structureBuilder, view.name, editScope.name);
                }
            }
        }
    }
    addNavigateEvent(structureBuilder, viewName, scopeName, targetViewName, label, eventPosition = 'actions') {
        structureBuilder.addEvent({
            eventName: `${viewName}NavigateTo${targetViewName}`,
            eventType: 'navigate',
            eventLabel: label,
            eventPosition: eventPosition,
            sourceViewName: viewName,
            sourceScopeName: scopeName,
            targetViewName,
        });
    }
    addPersistEvent(structureBuilder, viewName) {
        structureBuilder.addEvent({
            eventName: `${viewName}Persist`,
            eventType: 'persist',
            eventLabel: 'Save data',
            eventPosition: 'actions',
            sourceViewName: viewName,
        });
    }
    addDeleteEvent(structureBuilder, viewName, scopeName) {
        structureBuilder.addEvent({
            eventName: `${viewName}Delete`,
            eventType: 'delete',
            eventLabel: 'Delete',
            eventPosition: 'actions',
            sourceViewName: viewName,
            sourceScopeName: scopeName,
        });
    }
    isFieldSkippable(field) {
        return ['id', 'createdAt', 'updatedAt', 'deletedAt'].includes(field.name);
    }
}
export const printStructureStmts = (app) => {
    const printer = new AppStructureStmtPrinter();
    return printer.print(app);
};
export const printCrudStructureStmts = (schema, schemaExtensions, applicationType) => {
    const printer = new AppStructureStmtPrinter();
    return printer.printCRUD(schema, schemaExtensions, applicationType);
};
export const printStructureForms = (structure) => {
    if (!structure) {
        return null;
    }
    let result = 'forms:\n';
    for (const form of Object.values(structure.forms)) {
        result += ` ${form.name}:\n`;
        for (const field of Object.values(form.fields)) {
            result += `  - ${field.name} (${field.type})\n`;
        }
    }
    return result;
};
export const printStructureReadouts = (structure) => {
    if (!structure) {
        return null;
    }
    let result = 'readouts:\n';
    for (const readout of Object.values(structure.readouts)) {
        const fields = Object.values(readout.fields);
        if (!fields.length) {
            continue;
        }
        result += ` ${readout.name}:\n`;
        for (const field of fields) {
            result += `  - ${field.name}\n`;
        }
    }
    return result;
};
export const printStructurePreviews = (structure) => {
    if (!structure) {
        return null;
    }
    let result = 'previews:\n';
    for (const preview of Object.values(structure.previews)) {
        const fields = Object.values(preview.fields);
        if (!fields.length) {
            continue;
        }
        result += ` ${preview.name}:\n`;
        for (const field of fields) {
            result += `  - ${field.field}\n`;
        }
    }
    return result;
};
export const printStructureNavigationItem = (navigationItem) => {
    let result = '';
    result += `     - ${navigationItem.label}\n`;
    if (navigationItem.viewName) {
        result += `       - viewName: ${navigationItem.viewName}\n`;
    }
    result += `       - icon: ${navigationItem.icon ?? 'dot-icon'}\n`;
    if (navigationItem.children?.length) {
        result += '       - children:\n';
        result += navigationItem.children?.map(printStructureNavigationItem).join('');
    }
    return result;
};
export const printStructureNavigation = (structure) => {
    if (!structure) {
        return null;
    }
    let result = 'navigation:\n';
    for (const item of structure.navigation) {
        result += ` - ${item.label}\n`;
        result += `   - viewName: ${item.viewName}\n`;
        result += `   - icon: ${item.icon ?? 'dot-icon'}\n`;
        if (item.children?.length) {
            result += '   - children:\n';
            result += item.children?.map(printStructureNavigationItem).join('');
        }
    }
    return result;
};
export const printStructure = (structure) => {
    if (!structure) {
        return null;
    }
    let result = 'views:\n';
    for (const view of Object.values(structure.views)) {
        result += ` ${view.name}:\n`;
        if (view.events && Object.keys(view.events)?.length > 0) {
            result += ' - events:\n';
            for (const event of Object.values(view.events)) {
                result += `  - ${event.name}:\n`;
                result += `    - type: ${event.type}\n`;
                result += `    - label: ${event.label}\n`;
                result += `    - position: ${event.position}\n`;
                if (isNavigateEvent(event)) {
                    result += `    - targetViewName: ${event.targetViewName}`;
                }
                result += '\n';
            }
        }
        result += ' - scopes:\n';
        for (const scope of Object.values(view.scopes)) {
            result += `  - ${scope.name} (${scope.type})\n`;
            result += `    - entity: ${scope.entityName}\n`;
            if (isDataGridScope(scope)) {
                result += '    - columns:\n';
                for (const column of Object.values(scope.columns ?? {})) {
                    result += `      - ${column.field} (${column.type})\n`;
                }
            }
            if (isDetailScope(scope)) {
                result += '    - readouts:\n';
                for (const readout of scope.readouts ?? []) {
                    result += `      - ${readout}\n`;
                }
            }
            if (isCreateScope(scope) || isEditScope(scope)) {
                result += '    - forms:\n';
                for (const form of scope.forms ?? []) {
                    result += `      - ${form}\n`;
                }
            }
        }
        result += '\n';
    }
    const forms = printStructureForms(structure);
    const details = printStructureReadouts(structure);
    const previews = printStructurePreviews(structure);
    result += forms ? forms + '\n' : '';
    result += details ? details + '\n' : '';
    result += previews ? previews + '\n' : '';
    result += printStructureNavigation(structure);
    return result;
};
export function getMatchingOperations({ operations, predicate, numberOfEntityFieldsWithoutId, }) {
    const result = {};
    for (const op of ['read', 'create', 'update']) {
        const fields = Object.entries(operations?.[op] ?? {})
            .filter(([, it]) => it === predicate)
            .map(([it]) => it);
        if (fields.length === 0) {
            // biome-ignore lint/correctness/noUnnecessaryContinue: keep the original code
            continue;
        }
        else if (fields.length === numberOfEntityFieldsWithoutId) {
            result[op] = true;
        }
        else {
            result[op] = fields;
        }
    }
    if (operations?.delete === predicate) {
        result.delete = true;
    }
    return result;
}
//# sourceMappingURL=printStructure.js.map